import { useEffect, useState } from "react";
import type { ColorSemantico } from "../../../shared/types";
import { SelectorColorSemantico } from "../../components/SelectorColorSemantico";
import { useToast } from "../../components/Toast";
import { api, ErrorApi } from "../../lib/apiClient";

interface ItemConColor {
  id: string;
  nombre: string;
  color: ColorSemantico | null;
}

// Materia/Fuero son catálogos de PLATAFORMA (sin tenant_id, administrados
// sólo por SUPERADMIN) — este panel no crea/edita Materia/Fuero en sí, sólo
// el color que ESTE estudio les asigna (override tenant-scoped, ver
// migrations/0013_colores_semanticos.sql). Un solo componente parametrizado
// por `tipo`, reutilizado dos veces desde ConfiguracionPage — no dos
// paneles duplicados.
export function PanelColorCatalogo({
  titulo,
  descripcion,
  tipo,
  puedeAdministrar,
}: {
  titulo: string;
  descripcion: string;
  tipo: "materia" | "fuero";
  puedeAdministrar: boolean;
}) {
  const { mostrar } = useToast();
  const [items, setItems] = useState<ItemConColor[]>([]);
  const [cargando, setCargando] = useState(true);
  const endpointLectura = tipo === "materia" ? "materias" : "fueros";

  async function cargar() {
    setCargando(true);
    try {
      const datos = await api.get<{ items: ItemConColor[] }>(`/catalogos/${endpointLectura}`);
      setItems(datos.items);
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo cargar el catálogo.", "error");
    } finally {
      setCargando(false);
    }
  }

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo]);

  async function cambiarColor(id: string, color: ColorSemantico) {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, color } : i)));
    try {
      await api.put(`/catalogos-estudio/colores/${tipo}/${id}`, { color });
    } catch (err) {
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo guardar el color.", "error");
      cargar();
    }
  }

  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-texto">{titulo}</h3>
        <p className="text-xs text-texto-secundario">{descripcion}</p>
      </div>
      {cargando ? (
        <p className="text-sm text-texto-secundario">Cargando…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-texto-secundario">Sin ítems en el catálogo.</p>
      ) : (
        <ul className="divide-y divide-borde rounded-lg border border-borde">
          {items.map((item) => (
            <li key={item.id} className="flex items-center justify-between gap-4 px-4 py-3">
              <span className="text-sm text-texto">{item.nombre}</span>
              {puedeAdministrar ? (
                <SelectorColorSemantico valor={item.color} onCambiar={(color) => cambiarColor(item.id, color)} />
              ) : (
                <span className="text-xs text-texto-secundario">{item.color ?? "Sin color"}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
